import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import type { WidgetLayoutEntry } from "./widget-registry.js";

/**
 * Loads, holds and persists the employee's saved dashboard arrangement.
 *
 * Single responsibility: this hook knows about *storage* only. It has no opinion
 * on what widgets exist (the registry's job) or how they are arranged on screen
 * (the grid's job) — it moves a list of layout entries to and from the server.
 */

interface LayoutResponse {
  widgets: WidgetLayoutEntry[];
}

export interface DashboardLayoutStore {
  saved: WidgetLayoutEntry[];
  loading: boolean;
  /** Set when the last save failed, so the UI can say the layout isn't stored. */
  saveError: string;
  /** Persists the arrangement; optimistic, debounced, and reverted on failure. */
  save: (entries: WidgetLayoutEntry[]) => void;
  /** Clears the saved layout so the registry defaults apply again. */
  reset: () => Promise<void>;
}

// Dragging emits a save per drop and the size menu one per click. Debouncing
// collapses a flurry of adjustments into a single write.
const SAVE_DEBOUNCE_MS = 600;

export function useDashboardLayout(): DashboardLayoutStore {
  const [saved, setSaved] = useState<WidgetLayoutEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState("");

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<WidgetLayoutEntry[] | null>(null);
  // The last arrangement the server confirmed, so a failed save can roll back to
  // something real rather than to the defaults.
  const confirmed = useRef<WidgetLayoutEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.get<LayoutResponse>("/me/dashboard-layout")
      .then((res) => {
        if (cancelled) return;
        const entries = res.data.widgets ?? [];
        setSaved(entries);
        confirmed.current = entries;
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // A failed load is not fatal: the registry defaults still render. The
        // employee just sees the standard dashboard this time.
        logError("useDashboardLayout.load", e);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const flush = useCallback(() => {
    const entries = pending.current;
    if (!entries) return;
    pending.current = null;

    api.put<LayoutResponse>("/me/dashboard-layout", { widgets: entries })
      .then((res) => {
        confirmed.current = res.data.widgets ?? entries;
        setSaveError("");
      })
      .catch((e: unknown) => {
        logError("useDashboardLayout.save", e);
        setSaved(confirmed.current);
        setSaveError("Your layout couldn't be saved. The last saved arrangement has been restored.");
      });
  }, []);

  const save = useCallback((entries: WidgetLayoutEntry[]) => {
    setSaved(entries);          // optimistic: the grid already shows this
    pending.current = entries;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }, [flush]);

  // A pending change must not be lost to an unmount (navigating away straight
  // after a drag), so send it immediately instead of letting the timer die.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (pending.current) flush();
  }, [flush]);

  const reset = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    pending.current = null;
    try {
      await api.delete("/me/dashboard-layout");
      setSaved([]);
      confirmed.current = [];
      setSaveError("");
    } catch (e) {
      logError("useDashboardLayout.reset", e);
      setSaveError("Couldn't reset your layout. Please try again.");
    }
  }, []);

  return { saved, loading, saveError, save, reset };
}
